import { useMemo } from 'react';
import FormGroup from '~/components/Form/FormGroup';
import TextField from '~/components/Form/TextField';
import useTranslation from '~/hooks/useTranslation';
import Radio from '~/components/Form/Radio';
import mainBookerStyles from '~/styles/bookingFlow/main-booker.module.css';
import DateField from '~/components/Form/DateField';
import useGenderOptions from '~/hooks/bookingFlow/useGenderOptions';
import PhoneNumberField from '~/components/Form/PhoneNumberField';
import { useOptions } from '~/hooks/useOptions';
import { InferredBookingFlowOptions } from '~/types/options';
import InformationCard from './InformationCard';
import ParticipantNotes from './ParticipantNotes';

type Props = {
  registerFieldPath: string;
  participantsFieldPath: string;
  className?: string;
};

export default function MainBooker({
  registerFieldPath,
  participantsFieldPath,
  className,
}: Props) {
  const translate = useTranslation();
  const genderOptions = useGenderOptions();
  const options = useOptions() as InferredBookingFlowOptions;

  const mandatoryFields = useMemo(() => {
    return options.mandatoryFields?.split(',').map((field) => field.trim());
  }, [options.mandatoryFields]);

  const mainBookerMaxDate = new Date(new Date().setFullYear(new Date().getFullYear() - 18));

  return (
    <InformationCard
      title={translate('bookingFlow.information.main_booker_title')}
      description={translate('bookingFlow.information.main_booker_description')}
      className={className}
    >
      <FormGroup>
        <Radio
          label={translate('form.gender')}
          items={genderOptions}
          registerField={`${participantsFieldPath}.gender`}
          required
        />
      </FormGroup>

      <div className={mainBookerStyles.mainBookerName}>
        <FormGroup>
          <TextField
            label={translate('form.first_name')}
            fieldType="text"
            registerField={`${participantsFieldPath}.first_name`}
            min={1}
            max={200}
            required
          />
        </FormGroup>

        <FormGroup>
          <TextField
            label={translate('form.last_name')}
            fieldType="text"
            registerField={`${participantsFieldPath}.last_name`}
            min={1}
            max={200}
            required
          />
        </FormGroup>
      </div>

      {options.passportFields && (
        <FormGroup>
          <TextField
            label={translate('form.passport_number')}
            fieldType="text"
            registerField={`${participantsFieldPath}.passport_number`}
            min={1}
            max={200}
            required={mandatoryFields?.includes('passport_number') ?? false}
          />
        </FormGroup>
      )}

      <FormGroup>
        <DateField
          label={translate('form.birth_date')}
          registerField={`${participantsFieldPath}.birth_date`}
          max={mainBookerMaxDate.toISOString().split('T')[0]}
          required
        />
      </FormGroup>

      <FormGroup>
        <TextField
          label={translate('form.email')}
          fieldType="email"
          registerField={`${registerFieldPath}.email`}
          min={1}
          max={200}
          required
        />
      </FormGroup>

      <FormGroup>
        <PhoneNumberField
          label={translate('form.phone')}
          numberField={`${registerFieldPath}.phone`}
          countryCodeField={`${registerFieldPath}.phone_country_code`}
          required
        />
      </FormGroup>

      <FormGroup>
        <PhoneNumberField
          label={translate('form.phone_trip')}
          numberField={`${registerFieldPath}.phone_trip`}
          countryCodeField={`${registerFieldPath}.phone_trip_country_code`}
          required={mandatoryFields?.includes('phone_trip') ?? false}
        />
      </FormGroup>

      <ParticipantNotes
        registerFieldPath={participantsFieldPath}
      />
    </InformationCard>
  );
}
